import type { ColorKind, ShapeKind } from '../assets/art/ids'
import { shuffled } from './shuffle'

export const LEVEL_COUNT = 5
export const FREE_PLAY_LEVEL = 5

export type SortBy = 'shape' | 'color'
export type ColorTrayColor = Exclude<ColorKind, 'green'>

export type LevelTray =
  | { kind: 'shape'; shape: ShapeKind }
  | { kind: 'color'; color: ColorTrayColor }

export type LevelPiece = {
  id: string
  shape: ShapeKind
  color: ColorKind
  size: 'big' | 'normal'
  scale: number
  rotateDeg: number
}

export type LevelDef = {
  id: number
  sortBy: SortBy
  trays: readonly LevelTray[]
  pieces: readonly LevelPiece[]
}

function piece(
  id: string,
  shape: ShapeKind,
  color: ColorKind,
  extras: Partial<Pick<LevelPiece, 'size' | 'scale' | 'rotateDeg'>> = {},
): LevelPiece {
  return {
    id,
    shape,
    color,
    size: extras.size ?? 'normal',
    scale: extras.scale ?? 1,
    rotateDeg: extras.rotateDeg ?? 0,
  }
}

export const LEVELS: readonly LevelDef[] = [
  {
    id: 1,
    sortBy: 'shape',
    trays: [
      { kind: 'shape', shape: 'circle' },
      { kind: 'shape', shape: 'square' },
    ],
    pieces: [
      piece('circle-red-1', 'circle', 'red', { size: 'big' }),
      piece('circle-red-2', 'circle', 'red', { size: 'big' }),
      piece('square-yellow-1', 'square', 'yellow', { size: 'big' }),
      piece('square-yellow-2', 'square', 'yellow', { size: 'big' }),
    ],
  },
  {
    id: 2,
    sortBy: 'shape',
    trays: [
      { kind: 'shape', shape: 'circle' },
      { kind: 'shape', shape: 'square' },
      { kind: 'shape', shape: 'triangle' },
    ],
    pieces: [
      piece('circle-red-1', 'circle', 'red'),
      piece('circle-red-2', 'circle', 'red'),
      piece('square-yellow-1', 'square', 'yellow'),
      piece('square-yellow-2', 'square', 'yellow'),
      piece('triangle-blue-1', 'triangle', 'blue'),
      piece('triangle-blue-2', 'triangle', 'blue'),
    ],
  },
  {
    id: 3,
    sortBy: 'shape',
    trays: [
      { kind: 'shape', shape: 'circle' },
      { kind: 'shape', shape: 'square' },
      { kind: 'shape', shape: 'triangle' },
    ],
    pieces: [
      piece('circle-red-1', 'circle', 'red'),
      piece('circle-yellow-1', 'circle', 'yellow'),
      piece('circle-blue-1', 'circle', 'blue'),
      piece('square-yellow-1', 'square', 'yellow'),
      piece('square-blue-1', 'square', 'blue'),
      piece('square-green-1', 'square', 'green'),
      piece('triangle-red-1', 'triangle', 'red'),
      piece('triangle-green-1', 'triangle', 'green'),
      piece('triangle-blue-1', 'triangle', 'blue'),
    ],
  },
  {
    id: 4,
    sortBy: 'color',
    trays: [
      { kind: 'color', color: 'red' },
      { kind: 'color', color: 'yellow' },
      { kind: 'color', color: 'blue' },
    ],
    pieces: [
      piece('circle-red-1', 'circle', 'red'),
      piece('square-red-1', 'square', 'red'),
      piece('triangle-red-1', 'triangle', 'red'),
      piece('circle-yellow-1', 'circle', 'yellow'),
      piece('square-yellow-1', 'square', 'yellow'),
      piece('triangle-yellow-1', 'triangle', 'yellow'),
      piece('circle-blue-1', 'circle', 'blue'),
      piece('square-blue-1', 'square', 'blue'),
      piece('triangle-blue-1', 'triangle', 'blue'),
    ],
  },
  {
    id: 5,
    sortBy: 'shape',
    trays: [
      { kind: 'shape', shape: 'circle' },
      { kind: 'shape', shape: 'square' },
      { kind: 'shape', shape: 'triangle' },
    ],
    pieces: [
      piece('circle-red-1', 'circle', 'red', { scale: 1.06, rotateDeg: -8 }),
      piece('circle-yellow-1', 'circle', 'yellow', { scale: 1.0, rotateDeg: 6 }),
      piece('circle-blue-1', 'circle', 'blue', { scale: 1.04, rotateDeg: -4 }),
      piece('circle-green-1', 'circle', 'green', { scale: 1.12, rotateDeg: 8 }),
      piece('square-red-1', 'square', 'red', { scale: 1.0, rotateDeg: 7 }),
      piece('square-yellow-1', 'square', 'yellow', { scale: 1.05, rotateDeg: -6 }),
      piece('square-blue-1', 'square', 'blue', { scale: 1.08, rotateDeg: 4 }),
      piece('square-green-1', 'square', 'green', { scale: 1.15, rotateDeg: -8 }),
      piece('triangle-red-1', 'triangle', 'red', { scale: 1.03, rotateDeg: 5 }),
      piece('triangle-yellow-1', 'triangle', 'yellow', { scale: 1.1, rotateDeg: -7 }),
      piece('triangle-blue-1', 'triangle', 'blue', { scale: 1.07, rotateDeg: 8 }),
      piece('triangle-green-1', 'triangle', 'green', { scale: 1.0, rotateDeg: -3 }),
    ],
  },
]

export function levelById(id: number): LevelDef {
  const found = LEVELS.find((level) => level.id === id)
  return found ?? LEVELS[0]!
}

export function clampLevel(id: number): number {
  if (!Number.isInteger(id) || id < 1) return 1
  if (id > LEVEL_COUNT) return FREE_PLAY_LEVEL
  return id
}

export function advanceLevel(current: number): { level: number; shuffle: boolean } {
  if (current >= FREE_PLAY_LEVEL) return { level: FREE_PLAY_LEVEL, shuffle: true }
  return { level: current + 1, shuffle: false }
}

export function piecesForBoard(level: LevelDef, shuffle: boolean, rand: () => number = Math.random): LevelPiece[] {
  return shuffle ? shuffled(level.pieces, rand) : [...level.pieces]
}

export function artKeyFor(shape: ShapeKind, color: ColorKind): `${ShapeKind}-${ColorKind}` {
  return `${shape}-${color}`
}
